import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMixer } from '../context/MixerContext';
import PerspectiveIndicator from './PerspectiveIndicator';
import './MixerControls.css';

function MixerSlider({ 
  label, 
  leftLabel, 
  rightLabel, 
  value, 
  onChange,
  color = 'var(--accent-primary)'
}) {
  const handleChange = (e) => {
    onChange(parseInt(e.target.value, 10));
  };
  
  const position = value / 100;
  
  return (
    <div className="mixer-slider">
      <div className="mixer-slider__header">
        <span className="mixer-slider__label">{label}</span>
        <span className="mixer-slider__value">{value}</span>
      </div>
      
      <div className="mixer-slider__track-container">
        <div className="mixer-slider__endpoints">
          <span 
            className="mixer-slider__endpoint mixer-slider__endpoint--left"
            style={{ opacity: 1 - position * 0.5 }}
          >
            {leftLabel}
          </span>
          <span 
            className="mixer-slider__endpoint mixer-slider__endpoint--right"
            style={{ opacity: 0.5 + position * 0.5 }}
          >
            {rightLabel}
          </span>
        </div>
        
        <div className="mixer-slider__track">
          <motion.div 
            className="mixer-slider__fill"
            style={{ 
              width: `${value}%`,
              background: `linear-gradient(90deg, var(--surface-elevated) 0%, ${color} 100%)`
            }}
            layoutId={`slider-fill-${label}`}
          />
          <input
            type="range"
            min="0"
            max="100"
            value={value}
            onChange={handleChange}
            className="mixer-slider__input"
          />
          <motion.div 
            className="mixer-slider__thumb"
            style={{ left: `calc(${value}% - 8px)` }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.95 }}
          />
        </div>
        
        <div className="mixer-slider__markers">
          {[0, 25, 50, 75, 100].map(mark => (
            <div 
              key={mark} 
              className={`mixer-slider__marker ${mark === 50 ? 'mixer-slider__marker--center' : ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ViewModeToggle({ viewMode, onToggle }) {
  return (
    <button 
      className={`view-mode-toggle ${viewMode === 'archive' ? 'view-mode-toggle--archive' : ''}`}
      onClick={onToggle}
    >
      <span className="view-mode-toggle__track">
        <motion.span 
          className="view-mode-toggle__indicator"
          animate={{ x: viewMode === 'archive' ? 32 : 0 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      </span>
      <span className="view-mode-toggle__labels">
        <span className={viewMode === 'live' ? 'active' : ''}>Live</span>
        <span className={viewMode === 'archive' ? 'active' : ''}>Archive</span>
      </span>
    </button>
  );
}

export default function MixerControls() {
  const { mixerState, updateSlider, viewMode, toggleViewMode, resetMixer } = useMixer();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };
  
  return (
    <motion.div 
      className={`mixer-controls ${isCollapsed ? 'mixer-controls--collapsed' : ''}`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      {isMobile && (
        <button 
          className="mixer-controls__collapse-toggle"
          onClick={toggleCollapse}
          aria-label={isCollapsed ? 'Expand mixer controls' : 'Collapse mixer controls'}
        >
          <span className="mixer-controls__collapse-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d={isCollapsed ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
            </svg>
          </span>
          <span className="mixer-controls__collapse-label">
            {isCollapsed ? 'Adjust Career Mixer' : 'Minimize'}
          </span>
        </button>
      )}
      
      <AnimatePresence>
        {(!isMobile || !isCollapsed) && (
          <motion.div
            className="mixer-controls__content"
            initial={isMobile ? { height: 0, opacity: 0 } : false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={isMobile ? { height: 0, opacity: 0 } : undefined}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="mixer-controls__header">
              <div className="mixer-controls__brand">
                <span className="mixer-controls__logo">⬡</span>
                <span className="mixer-controls__title">Career Mixer</span>
              </div>
              <ViewModeToggle viewMode={viewMode} onToggle={toggleViewMode} />
            </div>
            
            <div className="mixer-controls__sliders">
              <MixerSlider
                label="Focus"
                leftLabel="Technical"
                rightLabel="Business"
                value={mixerState.technicalVsBusiness}
                onChange={(v) => updateSlider('technicalVsBusiness', v)}
                color="var(--accent-technical)"
              />
              
              <MixerSlider
                label="Role"
                leftLabel="IC"
                rightLabel="Leadership"
                value={mixerState.icVsLeadership}
                onChange={(v) => updateSlider('icVsLeadership', v)}
                color="var(--accent-leadership)"
              />
              
              <MixerSlider
                label="Context"
                leftLabel="Consulting"
                rightLabel="Enterprise"
                value={mixerState.consultingVsEnterprise}
                onChange={(v) => updateSlider('consultingVsEnterprise', v)}
                color="var(--accent-enterprise)"
              />
            </div>
            
            <button className="mixer-controls__reset" onClick={resetMixer}>
              <span>Reset</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
            
            <PerspectiveIndicator />
            
            <div className="mixer-controls__footer">
              <span className="mixer-controls__hint">
                Adjust to explore different perspectives
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
